/**
 * Fleet domain constants — assets, devices, driver statuses, telematics providers.
 * Status keys map to StatusList table FK values on the backend.
 */

import bikeProfileImg from '@/assets/vehicles/bike-profile.png'

export const DEFAULT_PAGE_SIZE = 50

export const FLEET_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const

/** User-facing label for ride-hail integration (replaces "Uber" in fleet UI). */
export const PLATFORM_PARTNER_LABEL = 'Platform Partner'

export type AssetType = 'bike' | 'car' | 'cargo'

export interface AssetTypeConfig {
  key: AssetType
  label: string
  /** Material icon name for map markers. */
  mapIcon: string
  /** Placeholder image key for different UI contexts. */
  placeholders: {
    mobile: string
    profile: string
    assignedToDriver: string
  }
}

export const ASSET_TYPES: AssetTypeConfig[] = [
  {
    key: 'bike',
    label: 'Bike',
    mapIcon: 'pedal_bike',
    placeholders: { mobile: '/assets/placeholders/bike-mobile.svg', profile: bikeProfileImg, assignedToDriver: '/assets/placeholders/bike-assigned.svg' },
  },
  {
    key: 'car',
    label: 'Car',
    mapIcon: 'directions_car',
    placeholders: { mobile: '/assets/placeholders/car-mobile.svg', profile: '/assets/placeholders/car-profile.svg', assignedToDriver: '/assets/placeholders/car-assigned.svg' },
  },
  {
    key: 'cargo',
    label: 'Cargo',
    mapIcon: 'local_shipping',
    placeholders: { mobile: '/assets/placeholders/cargo-mobile.svg', profile: '/assets/placeholders/cargo-profile.svg', assignedToDriver: '/assets/placeholders/cargo-assigned.svg' },
  },
]

export const ASSET_TYPE_BY_KEY = Object.fromEntries(ASSET_TYPES.map((t) => [t.key, t])) as Record<AssetType, AssetTypeConfig>

export type TelematicsProviderKey = 'go_metro' | 'esquare'

export interface TelematicsProvider {
  key: TelematicsProviderKey
  label: string
}

export const TELEMATICS_PROVIDERS: TelematicsProvider[] = [
  { key: 'go_metro', label: 'Go Metro' },
  { key: 'esquare', label: 'ESquare' },
]

export const TELEMATICS_PROVIDER_BY_KEY = Object.fromEntries(TELEMATICS_PROVIDERS.map((p) => [p.key, p])) as Record<
  TelematicsProviderKey,
  TelematicsProvider
>

/** StatusList domain keys — backend resolves label and auto-transition rules. */
export type StatusListDomain = 'driver' | 'recruitment' | 'asset' | 'device'

export interface StatusListItem {
  id: number
  domain: StatusListDomain
  key: string
  label: string
  /** Badge colour token for VaBadge. */
  color: string
  /** When true, status is set automatically by backend activity hooks. */
  is_system_managed?: boolean
}

/** Driver fleet statuses (activity-driven where noted). */
export const DRIVER_STATUS_LIST: StatusListItem[] = [
  { id: 1, domain: 'driver', key: 'applying', label: 'Applying', color: 'info' },
  { id: 2, domain: 'driver', key: 'approved_not_assigned', label: 'Approved - Not Assigned', color: 'warning', is_system_managed: true },
  { id: 3, domain: 'driver', key: 'approved_assigned', label: 'Approved - Assigned', color: 'success', is_system_managed: true },
  { id: 4, domain: 'driver', key: 'active', label: 'Active', color: 'success' },
  { id: 5, domain: 'driver', key: 'suspended', label: 'Suspended', color: 'danger' },
  { id: 6, domain: 'driver', key: 'offboarded', label: 'Offboarded', color: 'secondary' },
]

/** Recruitment pipeline statuses. */
export const RECRUITMENT_STATUS_LIST: StatusListItem[] = [
  { id: 10, domain: 'recruitment', key: 'applying', label: 'Applying', color: 'info' },
  { id: 11, domain: 'recruitment', key: 'approved_not_assigned', label: 'Approved - Not Assigned', color: 'warning' },
  { id: 12, domain: 'recruitment', key: 'approved_assigned', label: 'Approved - Assigned', color: 'success' },
]

export const DRIVER_STATUS_BY_KEY = Object.fromEntries(DRIVER_STATUS_LIST.map((s) => [s.key, s])) as Record<
  string,
  StatusListItem
>

export const RECRUITMENT_STATUS_BY_KEY = Object.fromEntries(RECRUITMENT_STATUS_LIST.map((s) => [s.key, s])) as Record<
  string,
  StatusListItem
>
